import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { TrendingUp, Inbox, PhoneCall, CheckCircle2, Download, FileSpreadsheet, Stethoscope, Users, BellRing, Share2, Repeat, RefreshCw, CalendarDays, MapPin, XCircle, Ruler, AlertTriangle } from "lucide-react";
import { AdminTabs } from "@/components/AdminTabs";
import { DateRangeFilter } from "@/components/DateRangeFilter";
import { downloadCSV } from "@/lib/csv";
import { ServiceCoverageGrid } from "@/components/admin/ServiceCoverageGrid";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid, ResponsiveContainer } from "recharts";

const Stat = ({ icon: Icon, label, value, suffix, testid, accent }) => (
  <div className="card-soft" data-testid={testid}>
    <div className="flex items-center gap-2 text-[#5C6B64] text-xs uppercase tracking-wide">
      <Icon className={`w-4 h-4 ${accent || "text-[#2D6A4F]"}`} /> {label}
    </div>
    <div className="font-heading text-3xl text-[#1E3F33] mt-2">{value}{suffix || ""}</div>
  </div>
);

const SectionTable = ({ title, icon: Icon, columns, rows, testid, empty }) => (
  <div className="mt-8" data-testid={testid}>
    <h2 className="text-lg font-heading text-[#1E3F33] flex items-center gap-2 mb-3"><Icon className="w-5 h-5 text-[#2D6A4F]" /> {title}</h2>
    {rows.length === 0 ? (
      <p className="text-sm text-[#8A9A86]">{empty}</p>
    ) : (
      <div className="overflow-auto max-h-80 rounded-xl border border-[#1E3F33]/10">
        <table className="w-full text-sm">
          <thead className="bg-[#F0EFEA] text-left text-[#1E3F33] sticky top-0"><tr>
            {columns.map((c) => <th key={c.key} className="px-4 py-2.5 font-medium">{c.label}</th>)}
          </tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-[#1E3F33]/8">
                {columns.map((c) => <td key={c.key} className="px-4 py-2.5 text-[#5C6B64]">{r[c.key]}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const EXPORT_SECTIONS = [
  { key: "collectors", label: "Collectors", group: "Data tabs" },
  { key: "customers", label: "Customers", group: "Data tabs" },
  { key: "ads", label: "Ads", group: "Data tabs" },
  { key: "payments", label: "Payments", group: "Data tabs" },
  { key: "funnel", label: "Lead funnel totals", group: "Analytics" },
  { key: "lead_conversion", label: "Per-collector conversion", group: "Analytics" },
  { key: "metrics", label: "Analytics metrics", group: "Analytics" },
  { key: "declines", label: "Decline reasons", group: "Analytics" },
  { key: "referrals", label: "Monthly referrals", group: "Analytics" },
  { key: "geo", label: "Geographic demand", group: "Analytics" },
  { key: "coverage", label: "Coverage gaps", group: "Analytics" },
  { key: "service_coverage", label: "Service coverage (ZIP × service)", group: "Analytics" },
  { key: "promos", label: "Promo trial conversion", group: "Analytics" },
];

const DECLINE_LABELS = {
  too_far: "Too far away",
  wrong_service: "Service not offered",
  busy: "Too busy / unavailable",
  other: "Other",
  unspecified: "Unspecified",
};

const DECLINE_TREND_SERIES = [
  { key: "too_far", color: "#CC5A3A" },
  { key: "wrong_service", color: "#E9C46A" },
  { key: "busy", color: "#2D6A4F" },
  { key: "other", color: "#8A9A86" },
  { key: "unspecified", color: "#B8B2A3" },
];

export default function AdminLeadInsights() {
  const [data, setData] = useState(null);
  const [range, setRange] = useState({ start: "", end: "" });
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [sections, setSections] = useState({ collectors: true, customers: true, ads: true, payments: true, funnel: true, lead_conversion: true, metrics: true, declines: true, referrals: true, geo: true, coverage: true, service_coverage: true });

  const fetchData = useCallback((start, end) => {
    setData(null);
    api.get("/admin/analytics", { params: { start: start || undefined, end: end || undefined } })
      .then(({ data }) => setData(data))
      .catch((e) => { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); setData({ totals: { total: 0, contacted: 0, confirmed: 0, conversion_rate: 0 }, collectors: [], metrics: {}, monthly_referrals: [], geo_demand: [] }); });
  }, []);

  useEffect(() => { fetchData(range.start, range.end); }, [range, fetchData]);

  const exportCSV = () => {
    const rows = (data?.collectors || []).map((c) => ({
      collector: c.business_name || "",
      total_leads: c.total,
      contacted: c.contacted,
      confirmed: c.confirmed,
      conversion_rate_pct: c.conversion_rate,
    }));
    if (!rows.length) { toast.error("Nothing to export"); return; }
    downloadCSV("lead-insights.csv", rows);
    toast.success("CSV downloaded");
  };

  const downloadFullReport = async () => {
    setDownloadingReport(true);
    try {
      const selected = Object.keys(sections).filter((k) => sections[k]).join(",");
      const res = await api.get("/admin/report/full-export", {
        params: { start: range.start || undefined, end: range.end || undefined, sections: selected },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `admin-report-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Full report downloaded");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setDownloadingReport(false);
    }
  };

  const totals = data?.totals || { total: 0, contacted: 0, confirmed: 0, conversion_rate: 0 };
  const m = data?.metrics || {};

  return (
    <div className="container-x py-12" data-testid="admin-lead-insights">
      <AdminTabs />
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl text-[#1E3F33] font-semibold flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-[#2D6A4F]" /> Lead insights
          </h1>
          <p className="text-[#5C6B64] mt-2">Platform analytics: lead conversion, active users, referrals, geographic demand & subscription retention. Filter by any time period.</p>
        </div>
        <button
          data-testid="admin-insights-export-csv"
          onClick={exportCSV}
          disabled={!(data?.collectors || []).length}
          className="btn-secondary inline-flex items-center gap-2 text-sm py-1.5 px-3 disabled:opacity-50"
        >
          <Download className="w-4 h-4" /> Export collectors CSV
        </button>
      </div>

      <div className="my-6"><DateRangeFilter onChange={(start, end) => setRange({ start, end })} /></div>

      {/* Export builder */}
      <div className="card-soft" data-testid="admin-insights-export-builder">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-sm font-medium text-[#1E3F33] flex items-center gap-2"><FileSpreadsheet className="w-4 h-4 text-[#2D6A4F]" /> Full Excel report</div>
            <div className="mt-3 space-y-3">
              {["Data tabs", "Analytics"].map((grp) => (
                <div key={grp}>
                  <div className="text-[11px] uppercase tracking-wide text-[#8A9A86] mb-1.5">{grp}</div>
                  <div className="flex flex-wrap gap-4">
                    {EXPORT_SECTIONS.filter((s) => s.group === grp).map((s) => (
                      <label key={s.key} className="inline-flex items-center gap-2 text-sm text-[#5C6B64] cursor-pointer">
                        <input
                          type="checkbox"
                          data-testid={`export-section-${s.key}`}
                          checked={sections[s.key]}
                          onChange={(e) => setSections((p) => ({ ...p, [s.key]: e.target.checked }))}
                          className="rounded border-[#1E3F33]/30 text-[#2D6A4F] focus:ring-[#2D6A4F]"
                        />
                        {s.label}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-[#8A9A86] mt-3">Each checked section becomes a sheet in the workbook. Respects the date range above.</p>
          </div>
          <button
            data-testid="admin-full-report-export"
            onClick={downloadFullReport}
            disabled={downloadingReport || !Object.values(sections).some(Boolean)}
            className="btn-primary inline-flex items-center gap-2 text-sm py-2 px-4 disabled:opacity-50"
          >
            <FileSpreadsheet className="w-4 h-4" /> {downloadingReport ? "Preparing…" : "Download full report (Excel)"}
          </button>
        </div>
      </div>

      {data === null ? (
        <div className="mt-8 text-[#5C6B64]">Loading…</div>
      ) : (
        <>
          {/* Lead funnel */}
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat icon={Inbox} label="Total leads" value={totals.total} testid="admin-insight-total" accent="text-[#CC5A3A]" />
            <Stat icon={PhoneCall} label="Contacted" value={totals.contacted} testid="admin-insight-contacted" />
            <Stat icon={CheckCircle2} label="Confirmed" value={totals.confirmed} testid="admin-insight-confirmed" />
            <Stat icon={TrendingUp} label="Conversion" value={totals.conversion_rate} suffix="%" testid="admin-insight-conversion" />
          </div>

          {/* Platform metrics */}
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Stat icon={Stethoscope} label="Active collectors" value={m.active_collectors ?? 0} testid="admin-insight-active-collectors" />
            <Stat icon={Users} label="Active customers (period)" value={m.active_customers ?? 0} testid="admin-insight-active-customers" />
            <Stat icon={BellRing} label="Notify-consent customers" value={m.consent_customers ?? 0} testid="admin-insight-consent-customers" accent="text-[#CC5A3A]" />
            <Stat icon={Share2} label="Total referrals (period)" value={m.total_referrals ?? 0} testid="admin-insight-referrals" />
            <Stat icon={Repeat} label="Subscription retention" value={m.subscription_retention ?? 0} suffix="%" testid="admin-insight-retention" />
            <Stat icon={RefreshCw} label="Monthly renewal rate" value={m.monthly_renewal_rate ?? 0} suffix="%" testid="admin-insight-renewal" />
            <Stat icon={XCircle} label="Declined leads" value={totals.declined ?? 0} testid="admin-insight-declined" accent="text-[#CC5A3A]" />
            <Stat icon={RefreshCw} label="Re-matched after decline" value={totals.rematched ?? 0} testid="admin-insight-rematched" />
            <Stat icon={Ruler} label="Avg distance to customer" value={totals.avg_distance_miles ?? "—"} suffix={totals.avg_distance_miles != null ? " mi" : ""} testid="admin-insight-avg-distance" />
          </div>

          {/* Decline reasons */}
          <SectionTable
            title="Why collectors declined"
            icon={XCircle}
            testid="admin-insight-decline-reasons"
            columns={[{ key: "reason_label", label: "Reason" }, { key: "count", label: "Count" }]}
            rows={(data.decline_reasons || []).map((r) => ({ ...r, reason_label: DECLINE_LABELS[r.reason] || r.reason }))}
            empty="No declines in this period."
          />

          {/* Decline reason trends over time */}
          <div className="mt-8" data-testid="admin-insight-decline-trends">
            <h2 className="text-lg font-heading text-[#1E3F33] flex items-center gap-2 mb-3"><XCircle className="w-5 h-5 text-[#CC5A3A]" /> Decline reasons over time</h2>
            {(data.decline_trends || []).length === 0 ? (
              <p className="text-sm text-[#8A9A86]">No decline trend data in this period.</p>
            ) : (
              <div className="card-soft" style={{ height: 320 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={(data.decline_trends || []).map((row) => ({ month: row.month, ...Object.fromEntries(DECLINE_TREND_SERIES.map((s) => [s.key, row[s.key] || 0])) }))} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1E3F33" strokeOpacity={0.08} />
                    <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#5C6B64" }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#5C6B64" }} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    {DECLINE_TREND_SERIES.map((s) => (
                      <Bar key={s.key} dataKey={s.key} name={DECLINE_LABELS[s.key]} stackId="d" fill={s.color} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Monthly referrals */}
          <SectionTable
            title="Monthly referrals"
            icon={CalendarDays}
            testid="admin-insight-monthly-referrals"
            columns={[{ key: "month", label: "Month" }, { key: "count", label: "Referrals" }]}
            rows={data.monthly_referrals || []}
            empty="No referrals in this period."
          />

          {/* Geographic demand (state + ZIP combined) */}
          <SectionTable
            title="Geographic demand — by state & ZIP"
            icon={MapPin}
            testid="admin-insight-geo-demand"
            columns={[{ key: "state", label: "State" }, { key: "zip", label: "ZIP code" }, { key: "count", label: "Requests" }]}
            rows={data.geo_demand || []}
            empty="No geographic data in this period."
          />

          {/* Coverage gaps */}
          <div className="mt-8" data-testid="admin-insight-coverage-gaps">
            <h2 className="text-lg font-heading text-[#1E3F33] flex items-center gap-2 mb-1"><AlertTriangle className="w-5 h-5 text-[#CC5A3A]" /> Coverage gaps — by ZIP</h2>
            <p className="text-sm text-[#8A9A86] mb-3">ZIPs with strong demand but few active collectors (or high declines). Flagged rows may need collector recruitment.</p>
            {(data.coverage_gaps || []).length === 0 ? (
              <p className="text-sm text-[#8A9A86]">No coverage data in this period.</p>
            ) : (
              <div className="overflow-auto max-h-96 rounded-xl border border-[#1E3F33]/10">
                <table className="w-full text-sm">
                  <thead className="bg-[#F0EFEA] text-left text-[#1E3F33] sticky top-0"><tr>
                    {["State", "ZIP", "Requests", "Declines", "Active collectors", "Status"].map((h) => <th key={h} className="px-4 py-2.5 font-medium">{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {data.coverage_gaps.map((r) => (
                      <tr key={r.zip} className={`border-t border-[#1E3F33]/8 ${r.gap ? "bg-[#CC5A3A]/5" : ""}`} data-testid={`coverage-gap-row-${r.zip}`}>
                        <td className="px-4 py-2.5 text-[#5C6B64]">{r.state}</td>
                        <td className="px-4 py-2.5 text-[#1E3F33] font-medium">{r.zip}</td>
                        <td className="px-4 py-2.5 text-[#5C6B64]">{r.requests}</td>
                        <td className="px-4 py-2.5 text-[#5C6B64]">{r.declines}</td>
                        <td className="px-4 py-2.5 text-[#5C6B64]">{r.active_collectors}</td>
                        <td className="px-4 py-2.5">
                          {r.gap ? (
                            <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#CC5A3A]/15 text-[#CC5A3A] font-medium inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3"/> Gap</span>
                          ) : (
                            <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#2D6A4F]/12 text-[#2D6A4F] font-medium">Covered</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <ServiceCoverageGrid data={data.service_coverage} />

          {/* Per-collector conversion */}
          <div className="mt-8">
            <h2 className="text-lg font-heading text-[#1E3F33] flex items-center gap-2 mb-3"><TrendingUp className="w-5 h-5 text-[#2D6A4F]" /> Per-collector conversion</h2>
            {(data.collectors || []).length === 0 ? (
              <p className="text-sm text-[#8A9A86]" data-testid="admin-insights-empty">No leads in this period.</p>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-[#1E3F33]/10">
                <table className="w-full text-sm" data-testid="admin-insights-table">
                  <thead className="bg-[#F0EFEA] text-left text-[#1E3F33]"><tr>
                    <th className="px-4 py-2.5 font-medium">Collector</th>
                    <th className="px-4 py-2.5 font-medium">Leads</th>
                    <th className="px-4 py-2.5 font-medium">Contacted</th>
                    <th className="px-4 py-2.5 font-medium">Confirmed</th>
                    <th className="px-4 py-2.5 font-medium">Conversion</th>
                  </tr></thead>
                  <tbody>
                    {data.collectors.map((c) => (
                      <tr key={c.collector_id || c.business_name} className="border-t border-[#1E3F33]/8" data-testid={`admin-insight-row-${c.collector_id}`}>
                        <td className="px-4 py-2.5 font-medium text-[#1E3F33]">{c.business_name}</td>
                        <td className="px-4 py-2.5 text-[#5C6B64]">{c.total}</td>
                        <td className="px-4 py-2.5 text-[#5C6B64]">{c.contacted}</td>
                        <td className="px-4 py-2.5 text-[#5C6B64]">{c.confirmed}</td>
                        <td className="px-4 py-2.5 text-[#1E3F33]">{c.conversion_rate}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
